import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PenLine, Send } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { HouseRating, HouseLevelBadge } from "@/components/HouseRating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Reviews = () => {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", project: "", rating: 0, text: "" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (e) {
      /* keep section silent on load failure */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim() || form.rating < 1) {
      toast.error(t.reviews.toastRequired);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success(t.reviews.toastSuccess);
      setForm({ name: "", project: "", rating: 0, text: "" });
      setShowForm(false);
    } catch (err) {
      toast.error(t.reviews.toastError);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#1A1412] border border-[#D4AF37]/25 text-[#FDFBF7] px-5 py-4 text-sm placeholder-[#B8A99A]/60 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300";

  return (
    <section id="reviews" className="py-24 lg:py-32 bg-[#1A1412]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="text-[#D4AF37] tracking-[0.4em] uppercase text-sm mb-4">{t.reviews.eyebrow}</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[#FDFBF7] max-w-2xl" data-testid="reviews-heading">
              {t.reviews.heading}
            </h2>
            <p className="mt-6 text-[#B8A99A] max-w-xl text-sm md:text-base leading-relaxed">{t.reviews.sub}</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-3 bg-[#D4AF37] text-[#1A1412] px-6 py-3.5 text-xs tracking-widest uppercase font-semibold hover:bg-[#E5C15D] transition-colors duration-300"
            data-testid="write-review-button"
          >
            <PenLine className="h-4 w-4" /> {t.reviews.writeBtn}
          </button>
        </motion.div>

        {showForm && (
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-12 bg-[#2A211D] border border-[#D4AF37]/20 p-8 lg:p-10 space-y-6 max-w-2xl"
            data-testid="review-form"
          >
            <h3 className="font-heading text-2xl text-[#D4AF37]">{t.reviews.formTitle}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="review-name" className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.reviews.nameLabel}</label>
                <input
                  id="review-name"
                  data-testid="review-name-input"
                  className={inputCls}
                  placeholder={t.reviews.namePh}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="review-project" className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.reviews.projectLabel}</label>
                <input
                  id="review-project"
                  data-testid="review-project-input"
                  className={inputCls}
                  placeholder={t.reviews.projectPh}
                  value={form.project}
                  onChange={(e) => setForm({ ...form, project: e.target.value })}
                />
              </div>
            </div>
            <div>
              <span className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.reviews.ratingLabel}</span>
              <HouseRating
                value={form.rating}
                onChange={(r) => setForm({ ...form, rating: r })}
                descriptions={t.reviews.levels}
                legend={t.reviews.ratingLabel}
                pickHint={t.reviews.pickRating}
                testIdPrefix="review-rating"
              />
            </div>
            <div>
              <label htmlFor="review-text" className="block text-xs tracking-widest uppercase text-[#B8A99A] mb-2">{t.reviews.textLabel}</label>
              <textarea
                id="review-text"
                data-testid="review-text-input"
                rows={4}
                className={inputCls}
                placeholder={t.reviews.textPh}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="flex items-center gap-3 bg-[#D4AF37] text-[#1A1412] px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#E5C15D] transition-colors duration-300 disabled:opacity-60"
              data-testid="review-submit-button"
            >
              {sending ? t.reviews.sending : t.reviews.submit}
              <Send className="h-4 w-4" />
            </button>
          </motion.form>
        )}

        {reviews.length === 0 ? (
          <p className="mt-14 text-[#B8A99A] text-sm" data-testid="reviews-empty">{t.reviews.empty}</p>
        ) : (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="reviews-list">
            {reviews.map((r, i) => (
              <motion.blockquote
                key={r.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                className="border border-[#D4AF37]/20 bg-[#2A211D] p-8 flex flex-col gap-4 hover:-translate-y-1 hover:border-[#D4AF37]/50 transition-all duration-300"
                data-testid={`review-card-${r.id}`}
              >
                <HouseLevelBadge value={r.rating} />
                <p className="text-[#FDFBF7] text-sm leading-relaxed flex-1">"{r.text}"</p>
                <footer>
                  <span className="font-heading text-lg text-[#D4AF37]">{r.name}</span>
                  {r.project && <span className="block text-xs text-[#B8A99A] mt-1">{r.project}</span>}
                </footer>
              </motion.blockquote>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
